import { useState, useEffect, createContext, useContext } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})
const privilegedProfiles = ['super_admin', 'gdn_admin']

async function loadValidProfile(email) {
  const normalizedEmail = email?.trim().toLowerCase()
  if (!normalizedEmail) return null

  const { data, error } = await supabase
    .from('usuarios')
    .select('*')
    .eq('email', normalizedEmail)
    .eq('ativo', true)
    .maybeSingle()

  if (error) throw error
  if (!data) return null
  if (privilegedProfiles.includes(data.perfil)) return data
  if (!data.oficina_id) return null

  const { data: office, error: officeError } = await supabase
    .from('oficinas')
    .select('id')
    .eq('id', data.oficina_id)
    .maybeSingle()

  if (officeError) throw officeError
  return office ? data : null
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [perfil, setPerfil] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setUser(session?.user ?? null)
        if (session?.user) {
          buscarPerfil(session.user.email)
        } else {
          setLoading(false)
        }
      })
      .catch(async () => {
        await supabase.auth.signOut()
        setUser(null)
        setPerfil(null)
        setLoading(false)
      })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null)
        if (session?.user) {
          buscarPerfil(session.user.email)
        } else {
          setPerfil(null)
          setLoading(false)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  async function buscarPerfil(email) {
    try {
      const data = await loadValidProfile(email)
      if (!data) {
        await supabase.auth.signOut()
        setUser(null)
      }
      setPerfil(data)
    } catch (e) {
      console.log('Perfil nao encontrado')
      await supabase.auth.signOut()
      setUser(null)
      setPerfil(null)
    } finally {
      setLoading(false)
    }
  }

  async function signIn(email, password) {
    await supabase.auth.signOut()
    const { data, error } = await supabase.auth.signInWithPassword({
      email, password
    })
    if (error) throw error
    const profile = await loadValidProfile(data.user?.email || email)
    if (!profile) {
      await supabase.auth.signOut()
      throw new Error('Conta excluida, inativa ou sem oficina vinculada.')
    }
    setUser(data.user)
    setPerfil(profile)
    return { ...data, profile }
  }

  async function signOut() {
    await supabase.auth.signOut()
    setUser(null)
    setPerfil(null)
  }

  return (
    <AuthContext.Provider value={{ user, perfil, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
